import React, { useCallback, useEffect, useState, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { useQuery } from '@apollo/client';

import { routes } from 'lane-shared/config';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import { channelModuleByChannelId } from 'lane-shared/graphql/modules';
import { safeConvertToUUID } from 'uuid-encoding';
import { perPage } from 'lane-shared/helpers/constants/pagination';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { ContentTypeEnum } from 'constants-content';
import { getBrowserTimeZone } from '../../helpers/getChannelTimezone';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { toGraphQLContentType } from '../../../../../helpers/contentTypeMapper';

import { AdminPage, Input } from 'components';
import { Button } from 'design-system-web';
import { Subtitle } from 'components/typography';
import useQueryString from 'hooks/useQueryString';

import { searchServiceRequestsQuery } from 'graphql-queries';

import { MemberServiceRequestDetail } from './MemberServiceRequestDetail';
import { UciCard } from './UciCard';
import { useAutoSelection } from './useAutoSelection';

import styles from './MemberServiceRequestList.scss';
import { Channel, ContentType } from 'application-types';
import { LONG_DATE_TIME } from 'lane-shared/helpers/constants/dates';

export interface MemberServiceRequestListProps {
  content?: ContentType;
}

const DEBOUNCE_THROTTLE = 500;
let prevContent: string | undefined;

type serviceRequestListParams = {
  serviceRequestId: string;
  feedback?: string;
};

export const MemberServiceRequestList: React.FC<
  MemberServiceRequestListProps
> = ({ content }) => {
  // we need to preserve the list of service requests between re-renders, as
  // this component will need to show the previously loaded results
  // but if the user switches channels, we need to clear out the service requests
  if (content && content._id !== prevContent) {
    prevContent = content._id;
  }

  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, DEBOUNCE_THROTTLE);
  const [page, setPage] = useState(0);
  const loader = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] =
    useQueryString<serviceRequestListParams>({
      serviceRequestId: '',
    });
  const [serviceRequests, setServiceRequests] = useState([]);
  let total = 0;
  const handleObserver = useCallback((entries: any) => {
    const target = entries[0];

    if (target.isIntersecting && serviceRequests.length < total) {
      setPage(page + 1);
    }
  }, []);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);

    if (loader.current) observer.observe(loader.current);
  }, [handleObserver]);
  const channelId = content?.channel?._id
    ? safeConvertToUUID(content.channel._id)
    : '';
  const { data } = useQuery(searchServiceRequestsQuery, {
    variables: {
      channelId,
      search: {
        ...(debouncedSearch
          ? {
              search: {
                type: 'like',
                value: search,
              },
            }
          : {}),
      },
      pagination: {
        start: page * perPage,
        perPage,
      },
    },
  });

  const { data: moduleResp } = useQuery(channelModuleByChannelId, {
    variables: {
      channelId,
      contentType: toGraphQLContentType(ContentTypeEnum.WorkOrder),
    },
  });
  const settings: WorkOrderModuleSettings =
    moduleResp?.channelModuleByChannelId?.settings;

  total = data?.searchServiceRequests?.pageInfo?.total || 0;

  const { isAutoSelectionEnabled } = useAutoSelection({
    serviceRequests: data?.searchServiceRequests?.serviceRequests || [],
    searchParams,
  });

  useEffect(() => {
    setServiceRequests(data?.searchServiceRequests?.serviceRequests || []);

    if (isAutoSelectionEnabled) {
      const firstServiceRequestId =
        data?.searchServiceRequests?.serviceRequests?.[0]?.serviceRequestId;

      if (firstServiceRequestId) {
        setSearchParams({
          serviceRequestId: firstServiceRequestId,
        });
      }
    }
  }, [
    data,
    searchParams.serviceRequestId,
    searchParams.feedback,
    isAutoSelectionEnabled,
  ]);

  if (!content) {
    return <></>;
  }

  let timeZone;

  if (!content.geo) {
    timeZone = getBrowserTimeZone();
  } else {
    const [longitude, latitude] = content.geo;

    timeZone = getTimeZoneByGeoLocation({ longitude, latitude });
  }

  return (
    <AdminPage className={styles.AdminViewContent}>
      <div className={styles.headingContainer}>
        <span className={styles.heading}>
          {t`web.admin.serviceRequest.title`}
        </span>
        <Link to={routes.contentRenderer.replace(':id', content._id)}>
          <Button
            testId="create-button"
            variant="primary"
          >{t`web.member.serviceRequest.new.request`}</Button>
        </Link>
      </div>

      <div className={styles.workOrdersWindowContainer}>
        <div className={styles.workOrdrListContainer}>
          <Input
            className={styles.input}
            icon="search"
            value={search}
            onChange={val => {
              setSearch(val);
              setPage(0);
            }}
            onClear={() => {
              setSearch('');
            }}
            placeholder={t`web.member.serviceRequest.search.request`}
            testId="search-input"
          />
          {search && (
            <Subtitle>
              {t`web.member.serviceRequest.request.results.text`}{' '}
              <q>{search}</q>
            </Subtitle>
          )}
          {serviceRequests && serviceRequests.length > 0 ? (
            <>
              {serviceRequests.map((workOrder: any, index: number) => (
                <UciCard
                  id={workOrder.serviceRequestId}
                  key={index}
                  description={workOrder.description}
                  date={dateFormatter(
                    workOrder.createdAt,
                    LONG_DATE_TIME,
                    timeZone
                  )}
                  subTitle={workOrder.issue}
                  title={workOrder.category}
                  className={styles.uciCard}
                  isActive={
                    searchParams.serviceRequestId === workOrder.serviceRequestId
                  }
                  onSelect={id =>
                    setSearchParams({
                      serviceRequestId: id,
                    })
                  }
                  status={workOrder.status}
                  index={index}
                />
              ))}
              <div ref={loader} />
            </>
          ) : (
            <div className={styles.noResultsCard}>
              {search ? (
                <Subtitle>
                  {t`web.member.serviceRequest.request.noResults.text`}{' '}
                  <q>{search}</q>
                </Subtitle>
              ) : (
                <Subtitle>
                  {t`web.member.serviceRequest.noOpen.request.text`} <br />
                  {t`web.member.serviceRequest.yay.text`}
                </Subtitle>
              )}
            </div>
          )}
        </div>
        <div className={styles.workOrderDetailContainer}>
          {content.channel && (
            <MemberServiceRequestDetail
              serviceRequestId={searchParams.serviceRequestId}
              settings={settings}
              channel={content.channel as Channel}
              timeZone={timeZone}
            />
          )}
        </div>
      </div>
    </AdminPage>
  );
};
